/**
 * Staff & Regions — user management (accounts, roles, region assignment,
 * suspension, password resets) alongside region configuration.
 */
import { useCallback, useEffect, useState } from "react";
import { KeyRound, Loader2, Plus, ShieldOff, ShieldCheck } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency } from "@/lib/lendingCalculations";
import { staffApi } from "@/platform/staffApi";
import { useStaff } from "./StaffShell";

type Row = Record<string, any>;

const roleLabel = (role: string) => role.split("_").join(" ");

export function UsersPage() {
  const me = useStaff();
  const { toast } = useToast();
  const isAdmin = me?.role === "admin";

  const [staff, setStaff] = useState<Row[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [regions, setRegions] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  // Create
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("agent");
  const [regionIds, setRegionIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [credential, setCredential] = useState<{ email: string; password: string } | null>(null);

  // Edit / suspend
  const [editing, setEditing] = useState<Row | null>(null);
  const [suspending, setSuspending] = useState<Row | null>(null);
  const [reason, setReason] = useState("");

  // Region creation
  const [regionName, setRegionName] = useState("");
  const [regionCode, setRegionCode] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([staffApi.staffList(), staffApi.regions()]);
      setStaff(s.staff);
      setRoles(s.roles);
      setRegions(r.regions);
    } catch (error) {
      toast({ title: "Could not load users", description: error instanceof Error ? error.message : undefined, variant: "destructive" });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    setBusy(true);
    try {
      const created = await staffApi.createStaff({
        email, full_name: fullName, role, region_ids: regionIds,
      });
      setInviteOpen(false);
      setEmail(""); setFullName(""); setRegionIds([]);
      if (created.temporary_password) {
        setCredential({ email: created.email, password: created.temporary_password });
      }
      load();
    } catch (error) {
      toast({ title: "Could not create the account", description: error instanceof Error ? error.message : undefined, variant: "destructive" });
    } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await staffApi.updateStaff(editing.id, {
        role: editing.role,
        region_ids: editing.regions.map((r: Row) => r.id),
      });
      toast({ title: "Account updated" });
      setEditing(null);
      load();
    } catch (error) {
      toast({ title: "Could not update", description: error instanceof Error ? error.message : undefined, variant: "destructive" });
    } finally { setBusy(false); }
  };

  const toggleStatus = async (member: Row) => {
    if (member.status === "active") { setSuspending(member); return; }
    try {
      await staffApi.setStaffStatus(member.id, "active");
      toast({ title: `${member.full_name} reactivated` });
      load();
    } catch (error) {
      toast({ title: "Could not reactivate", description: error instanceof Error ? error.message : undefined, variant: "destructive" });
    }
  };

  const confirmSuspend = async () => {
    if (!suspending) return;
    setBusy(true);
    try {
      const res = await staffApi.setStaffStatus(suspending.id, "suspended", reason);
      toast({
        title: "Account suspended",
        description: `${res.sessions_revoked || 0} active session(s) ended immediately.`,
      });
      setSuspending(null); setReason("");
      load();
    } catch (error) {
      toast({ title: "Could not suspend", description: error instanceof Error ? error.message : undefined, variant: "destructive" });
    } finally { setBusy(false); }
  };

  const resetPassword = async (member: Row) => {
    try {
      const res = await staffApi.resetStaffPassword(member.id);
      if (res.temporary_password) {
        setCredential({ email: member.email, password: res.temporary_password });
      }
    } catch (error) {
      toast({ title: "Could not reset", description: error instanceof Error ? error.message : undefined, variant: "destructive" });
    }
  };

  const createRegion = async () => {
    try {
      await staffApi.createRegion(regionName.trim(), regionCode.trim());
      toast({ title: `Region created: ${regionName}` });
      setRegionName(""); setRegionCode("");
      load();
    } catch (error) {
      toast({ title: "Could not create region", description: error instanceof Error ? error.message : undefined, variant: "destructive" });
    }
  };

  if (loading) return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin" />;

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Staff & Regions</h1>
          <p className="text-sm text-muted-foreground">
            Accounts, roles, and which regions each person covers.
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setInviteOpen(true)}>
            <Plus className="mr-1 h-4 w-4" />Add staff
          </Button>
        )}
      </header>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Staff accounts</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>Role</TableHead><TableHead>Regions</TableHead>
              <TableHead>2FA</TableHead><TableHead>Status</TableHead>
              {isAdmin && <TableHead className="text-right">Actions</TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {staff.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>
                    <p className="font-medium">{s.full_name || "—"}</p>
                    <p className="text-xs text-muted-foreground">{s.email}</p>
                  </TableCell>
                  <TableCell className="capitalize">{roleLabel(s.role)}</TableCell>
                  <TableCell className="text-xs">
                    {s.regions.length === 0
                      ? <span className="text-warning">none assigned</span>
                      : s.regions.map((r: Row) => r.name).join(", ")}
                  </TableCell>
                  <TableCell>
                    {s.totp_enabled
                      ? <ShieldCheck className="h-4 w-4 text-success" />
                      : <span className="text-xs text-muted-foreground">off</span>}
                  </TableCell>
                  <TableCell>
                    <Badge className={s.status === "active"
                      ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}>
                      {s.status}
                    </Badge>
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setEditing({ ...s })}>Edit</Button>
                        <Button size="sm" variant="ghost" onClick={() => resetPassword(s)}
                          title="Reset password">
                          <KeyRound className="h-4 w-4" />
                        </Button>
                        {s.id !== me?.id && (
                          <Button size="sm" variant="ghost" onClick={() => toggleStatus(s)}
                            title={s.status === "active" ? "Suspend" : "Reactivate"}>
                            {s.status === "active"
                              ? <ShieldOff className="h-4 w-4 text-destructive" />
                              : <ShieldCheck className="h-4 w-4 text-success" />}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Regions</CardTitle>
          <CardDescription>A new branch is a form, not a deploy.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isAdmin && (
            <div className="flex gap-2">
              <Input placeholder="Name (e.g. Kitwe)" value={regionName}
                onChange={(e) => setRegionName(e.target.value)} className="max-w-xs" />
              <Input placeholder="Code" value={regionCode}
                onChange={(e) => setRegionCode(e.target.value.toUpperCase())} className="max-w-[8rem]" />
              <Button disabled={regionName.trim().length < 2 || regionCode.trim().length < 2}
                onClick={createRegion}>
                <Plus className="mr-1 h-4 w-4" />Create
              </Button>
            </div>
          )}
          <Table>
            <TableHeader><TableRow>
              <TableHead>Region</TableHead><TableHead>Code</TableHead>
              <TableHead>Customers</TableHead><TableHead>Staff</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {regions.map((r) => (
                <TableRow key={String(r.id)}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell>{r.code}</TableCell>
                  <TableCell className="tabular-nums">{r.customers}</TableCell>
                  <TableCell className="tabular-nums">{r.staff}</TableCell>
                  <TableCell>
                    <Badge className={r.active ? "bg-success/10 text-success" : "bg-muted"}>
                      {r.active ? "active" : "inactive"}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add staff */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add a staff account</DialogTitle>
            <DialogDescription>
              A temporary password is generated for you to hand over. They enroll 2FA themselves.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Full name</Label>
              <Input value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (
                    <SelectItem key={r} value={r} className="capitalize">{roleLabel(r)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Regions</Label>
              <div className="mt-1 flex flex-wrap gap-2">
                {regions.map((r) => {
                  const on = regionIds.includes(String(r.id));
                  return (
                    <Button key={String(r.id)} size="sm" variant={on ? "default" : "outline"}
                      onClick={() => setRegionIds(on
                        ? regionIds.filter((id) => id !== String(r.id))
                        : [...regionIds, String(r.id)])}>
                      {r.name}
                    </Button>
                  );
                })}
              </div>
              {regionIds.length === 0 && (
                <p className="mt-1 text-xs text-warning">
                  Without a region this person sees no clients.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
            <Button onClick={invite} disabled={busy || !email || fullName.length < 2}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit */}
      <Dialog open={Boolean(editing)} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.full_name}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Role</Label>
                <Select value={editing.role}
                  onValueChange={(v) => setEditing({ ...editing, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r} value={r} className="capitalize">{roleLabel(r)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Regions</Label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {regions.map((r) => {
                    const on = editing.regions.some((x: Row) => x.id === r.id);
                    return (
                      <Button key={String(r.id)} size="sm" variant={on ? "default" : "outline"}
                        onClick={() => setEditing({
                          ...editing,
                          regions: on
                            ? editing.regions.filter((x: Row) => x.id !== r.id)
                            : [...editing.regions, r],
                        })}>
                        {r.name}
                      </Button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend */}
      <Dialog open={Boolean(suspending)} onOpenChange={(open) => !open && setSuspending(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspend {suspending?.full_name}?</DialogTitle>
            <DialogDescription>
              Their live sessions end immediately and they cannot sign in again until reactivated.
            </DialogDescription>
          </DialogHeader>
          <Input placeholder="Reason (required)" value={reason}
            onChange={(e) => setReason(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspending(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmSuspend}
              disabled={busy || reason.trim().length < 3}>
              Suspend
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* One-time credential display */}
      <Dialog open={Boolean(credential)} onOpenChange={(open) => !open && setCredential(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Temporary password</DialogTitle>
            <DialogDescription>
              Shown once — copy it now and share it securely. They should change it after signing in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 rounded-lg border bg-muted/40 p-4 text-sm">
            <p><span className="text-muted-foreground">Email:</span> {credential?.email}</p>
            <p className="break-all">
              <span className="text-muted-foreground">Password:</span>{" "}
              <code className="rounded bg-background px-1">{credential?.password}</code>
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setCredential(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
